import { Dispatch, ReactNode, SetStateAction } from "react";
import { AddressBook } from "../../../Models/AddressBook";
import { ModalConfig } from "../../../Models/ModalConfig";
import { ErrorResponse } from "../../../Models/Error";

type ServiceType = "registry" | "logistic" | "shipment";

/* ===========================================================================
 * HANDLER CONFIGURATION TYPES
 * =========================================================================== */

/**
 * @interface HandlerConfig
 * Specifies configuration options for enabling or disabling
 * handler-related functionalities such as saving, searching, and billing.
 */
interface HandlerConfig {
  /**
   * Optional default values used to prefill the AddressBook form.
   */
  defaultValue?: Partial<AddressBook> | null;

  /**
   * Configuration options concerning registry-related features.
   */
  registry: {
    /**
     * Determines whether saving to the registry service is enabled.
     */
    saveEnabled: boolean;

    /**
     * Determines whether registry-based searching is enabled.
     */
    searchEnabled: boolean;
  };

  /**
   * Configuration options regarding billing-related features.
   */
  billing: {
    /**
     * Determines whether billing functionality is enabled.
     */
    enabled: boolean;
  };
}

/**
 * @const DEFAULT_HANDLER_CONFIG
 * Defines the standardized default configuration used when no custom
 * handlerConfig object is provided to the Provider.
 */
export const DEFAULT_HANDLER_CONFIG: HandlerConfig = {
  registry: {
    saveEnabled: false,
    searchEnabled: false,
  },
  billing: { enabled: true },
  defaultValue: null,
};

/* ===========================================================================
 * PROVIDER PROPS — INDEX PROVIDER
 * =========================================================================== */

/**
 * @interface AddressBookIndexProviderProps
 * Defines the properties accepted by the AddressBookIndexProvider component,
 * responsible for global-level configuration and contextual behavior.
 */
export interface AddressBookIndexProviderProps {
  /**
   * Child components to be rendered within the Provider.
   */
  children: ReactNode;

  /**
   * Specifies which backend service ("registry" or "logistic") is used.
   * Defaults to "registry".
   */
  service?: ServiceType;

  /**
   * Identifies the owner associated with the AddressBook entries.
   */
  ownerId?: string;

  /**
   * Indicates whether the address refers to the currently authenticated user.
   */
  myAddress?: boolean;

  /**
   * Optional callback executed after a successful action
   * (e.g., creation, update, or deletion).
   */
  callback?: () => void;

  /**
   * Optional configuration object defining enabled/disabled handler features.
   */
  handlerConfig?: HandlerConfig;
}

/**
 * @interface AddressBookIndexContextValue
 * Represents the values exposed by the AddressBookIndex context,
 * merging configuration, contextual metadata, and helper functions.
 */
export interface AddressBookIndexContextValue extends HandlerConfig {
  /**
   * Modal configuration object.
   */
  modalConfig: ModalConfig;

  /**
   * Setter for updating the modal configuration.
   */
  setModalConfig: Dispatch<SetStateAction<ModalConfig>>;

  /**
   * ID of the owner associated with the AddressBook (optional).
   */
  ownerId?: string;

  /**
   * Indicates whether the address belongs to the authenticated user.
   */
  myAddress: boolean;

  /**
   * Active service ("registry" or "logistic").
   */
  service: ServiceType;

  /**
   * Optional callback invoked after successful operations.
   */
  callback?: () => void;
}

/* ===========================================================================
 * PROVIDER PROPS — ADDRESSBOOK HANDLER PROVIDER
 * =========================================================================== */

/**
 * @interface AddressBookHandlerProviderProps
 * Defines the properties accepted by the AddressBookHandlerProvider component,
 * which manages state, validation, persistence, and synchronization of
 * AddressBook entities.
 */
export interface AddressBookHandlerProviderProps {
  /**
   * React children wrapped by the provider.
   * All descendant components will have access to AddressBook handler context.
   */
  children: ReactNode;

  /**
   * Optional ID of the AddressBook entity to be fetched and loaded on mount.
   * When omitted, the provider acts in "create" mode.
   */
  id?: string;

  /**
   * Identifier of the owner associated with the AddressBook.
   * Used for ownership validation and contextual UI behavior.
   */
  ownerId?: string;

  /**
   * Optional callback triggered after a successful create or update.
   * Receives the final AddressBook as an argument.
   */
  callback?: (addressBook?: AddressBook) => void;

  /**
   * Backend service to be used for AddressBook operations.
   * Defaults to "registry". Other supported services: "logistic", "shipment".
   */
  service?: ServiceType;

  /**
   * Indicates the current user action (e.g., "create", "edit", "details").
   * Determines UI behavior and input permissions.
   */
  action: string;

  /**
   * Initial loader state used to keep UI in a loading condition until data is ready.
   */
  preLoader?: boolean;

  /**
   * Optional initial values applied to the AddressBook form state.
   * Useful when prepopulating fields or injecting externally computed values.
   */
  defaultValue?: Partial<AddressBook> | null;

  /**
   * Enables autosave mode.
   * When true, changes are saved automatically without explicit user confirmation.
   */
  autosave?: boolean;

  /**
   * Billing-specific configuration.
   * Determines whether billing logic is enabled and may include
   * a linked billing AddressBook reference with corresponding callbacks.
   */
  billing?: {
    enabled: boolean;
    addressBook?: {
      id: string;
      service: ServiceType;
      enabled: boolean;
      onChange: (addressBook?: Partial<AddressBook>) => void;
    };
  };

  /**
   * Configuration object for registry-related features.
   * Controls permissions for save operations and search capabilities
   * when working with registry-service entities.
   */
  registry?: {
    saveEnabled: boolean;
    searchEnabled: boolean;
  };

  /**
   * Optional label displayed in the UI to provide contextual information.
   */
  label?: string;

  /**
   * When enabled, forces validation to run and display errors immediately.
   * Typically used when forms are validated externally before submission.
   */
  inError?: boolean;

  /**
   * Callback that receives the latest validation or backend errors.
   * Useful for external forms or composite components that need to react
   * to error state changes inside the provider.
   */
  getErrors?: (errors: ErrorResponse<AddressBook> | null) => void;
}

/**
 * @interface AddressBookHandlerContextValue
 * Represents the full state and operational functions exposed
 * by the AddressBookHandlerProvider.
 */
export interface AddressBookHandlerContextValue {
  /** Current AddressBook data (partial during editing). */
  data: Partial<AddressBook>;

  /** Error details originating from validation or HTTP errors. */
  error: ErrorResponse<AddressBook> | null;

  /** Indicates whether an asynchronous operation is in progress. */
  loader: boolean;

  /** Optional callback executed after successful operations. */
  callback?: (addressBook?: AddressBook) => void;

  /** Modal configuration object. */
  modalConfig: ModalConfig;

  /** Setter for updating modal configuration. */
  setModalConfig: Dispatch<SetStateAction<ModalConfig>>;

  /** Billing-related configuration. */
  billing: AddressBookHandlerProviderProps["billing"];

  /** Whether autosave mode is active. */
  autosave: boolean;

  /** Owner identifier linked to the AddressBook. */
  ownerId: string;

  /** Registry-related functionality configuration. */
  registry: AddressBookHandlerProviderProps["registry"];

  /** Optional contextual UI label. */
  label?: string;

  /**
   * Indicates whether the billing AddressBook differs
   * from the currently selected AddressBook.
   */
  billingAddressBookIsDifferentFromTheAddressBook: boolean;

  /**
   * Setter used to toggle the billing comparison state.
   */
  setBillingAddressBookIsDifferentFromTheAddressBook: Dispatch<
    SetStateAction<boolean>
  >;

  /** Action mode (e.g., "edit", "create", "view"). */
  action: string;

  /** Active service backend ("registry" or "logistic"). */
  service: ServiceType;

  /** Persists the AddressBook entity into the registry service. */
  saveRegistryAddressBook: () => void;

  /** Fetches an AddressBook by its ID. */
  getAddressBook: (id: string) => void;

  /**
   * Validates and saves an AddressBook.
   * @param addressBook The AddressBook entity.
   * @param property Optional field name for error clearing.
   * @param save Whether to persist immediately.
   */
  saveAddressBook: (
    addressBook: AddressBook,
    property: keyof AddressBook | null,
    save: boolean
  ) => void;
}

/* ===========================================================================
 * PROVIDER PROPS — DELETE PROVIDER
 * =========================================================================== */

/**
 * @interface AddressBookDeleteProviderProps
 * Properties accepted by the AddressBookDeleteProvider,
 * responsible for batch deletion of AddressBook entries.
 */
export interface AddressBookDeleteProviderProps {
  /** React children rendered inside the Provider. */
  children: ReactNode;

  /** List of AddressBook IDs to be deleted. Defaults to an empty array. */
  data?: string[];

  /** Optional callback executed after a successful deletion. */
  callback?: () => void;

  /** Backend service used for deletion. Defaults to "registry". */
  service?: ServiceType;
}

/**
 * @interface AddressBookDeleteContextValue
 * Represents the values exposed by the AddressBookDelete context.
 */
export interface AddressBookDeleteContextValue {
  /** Indicates whether the delete operation is in progress. */
  loader: boolean;

  /** Initiates deletion of all AddressBook IDs currently in `data`. */
  deleteAddressBooks: () => void;

  /** List of AddressBook IDs pending deletion. */
  data: string[];

  /** Optional callback invoked after successful deletion. */
  callback?: () => void;

  /** Active backend service used for deletion. */
  service: ServiceType;
}
